/**
 * The signed-in user's own account: profile, server-saved appearance and open
 * invitations addressed to their confirmed email. None of these expose other users.
 */
import type { Transport } from './transport.ts';

export const THEMES = ['system', 'light', 'dark'] as const;
export type Theme = typeof THEMES[number];
/** Background styles accepted by app.set_appearance. */
export const BACKGROUNDS = ['default', 'slate', 'mist', 'sand', 'forest', 'midnight', 'paper', 'ocean', 'lavender', 'graphite'] as const;
export type Background = typeof BACKGROUNDS[number];

export interface Appearance { theme: Theme; accent: string; background: Background; reducedMotion: boolean }
export interface Account {
  displayName: string | null;
  email: string;
  locale: string | null;
  timeZone: string | null;
  appearance: Appearance;
  /** Pass as `expected` when saving; 0 means nothing saved yet. */
  revision: number;
}

interface AccountRaw {
  display_name: string | null; email: string; locale: string | null; time_zone: string | null;
  theme: string; accent_hex: string; background_style: string; reduced_motion: boolean; revision: number;
}

export async function readMyAccount(transport: Transport, signal?: AbortSignal): Promise<Account | null> {
  const rows = await transport.rpc<AccountRaw[]>('read_my_account', {}, signal);
  const raw = rows[0];
  if (!raw) return null;
  return {
    displayName: raw.display_name, email: raw.email, locale: raw.locale, timeZone: raw.time_zone,
    appearance: {
      theme: (THEMES as readonly string[]).includes(raw.theme) ? raw.theme as Theme : 'system',
      accent: raw.accent_hex,
      background: (BACKGROUNDS as readonly string[]).includes(raw.background_style) ? raw.background_style as Background : 'default',
      reducedMotion: raw.reduced_motion,
    },
    revision: Number(raw.revision),
  };
}

/** Saves appearance with a revision check; a stale `expected` fails as revision_conflict. */
export async function saveAppearance(transport: Transport, expected: number, appearance: Appearance): Promise<number> {
  const next = await transport.rpc<number>('set_appearance', {
    expected, theme_choice: appearance.theme, accent: appearance.accent, background: appearance.background, motion_reduced: appearance.reducedMotion,
  });
  return Number(next);
}

export interface Invitation { id: string; workspaceId: string; workspaceName: string; roleName: string; createdAt: number; expiresAt: number }
export async function listMyInvitations(transport: Transport, signal?: AbortSignal): Promise<Invitation[]> {
  const rows = await transport.rpc<Array<{ invitation_id: string; workspace_id: string; workspace_name: string; role_name: string; created_at: string; expires_at: string }>>('list_my_invitations', {}, signal);
  return rows.map(r => ({ id: r.invitation_id, workspaceId: r.workspace_id, workspaceName: r.workspace_name, roleName: r.role_name, createdAt: Date.parse(r.created_at), expiresAt: Date.parse(r.expires_at) }));
}

/** Initials for avatars: first letters of the first two words, else the first two characters. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toLocaleUpperCase();
  const w = words[0] ?? '?';
  // Keep a digit after a letter (e.g. "5 Star" → "5S" is handled above; "A1" → "A1").
  return w.slice(0, 2).toLocaleUpperCase();
}

/** One earlier download of the caller's own account data ('delivered': handed to the device, nothing stored). */
export interface AccountExport { id: string; requestedAt: number; state: string }
/** The caller's own account data as one JSON document (no workspace business records; never the
 * scrambled sign-in network). Recorded once under `exportId`; 5 new exports an hour. */
export const exportMyAccount = (t: Transport, exportId: string, signal?: AbortSignal) =>
  t.rpc<unknown>('export_my_account', { export: exportId }, signal);
export async function listMyAccountExports(t: Transport, signal?: AbortSignal): Promise<AccountExport[]> {
  const rows = await t.rpc<{ id: string; requested_at: string; state: string }[]>('list_my_account_exports', { lim: 20 }, signal);
  return rows.map(r => ({ id: r.id, requestedAt: Date.parse(r.requested_at), state: r.state }));
}
/** Workspaces the caller owns: each must be handed over before the account can be deleted. */
export interface DeletionBlocker { workspaceId: string; workspaceName: string; reason: 'owner' }
export async function listMyAccountDeletionBlockers(t: Transport, signal?: AbortSignal): Promise<DeletionBlocker[]> {
  const rows = await t.rpc<{ workspace_id: string; workspace_name: string; reason: string }[]>('list_my_account_deletion_blockers', {}, signal);
  return rows.map(r => ({ workspaceId: r.workspace_id, workspaceName: r.workspace_name, reason: 'owner' }));
}
/** The downloaded file: readable JSON, named by date. */
export const accountExportFile = (doc: unknown, now: Date) => ({
  name: `miraops-account-data-${now.toISOString().slice(0, 10)}.json`,
  text: JSON.stringify(doc, null, 2) + '\n',
});
